import { yupResolver } from '@hookform/resolvers/yup';
import { Breadcrumb, Button } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';

import type { IContact } from '@/api/endpoints/mainPages';
import { useCreateContactMutation, useUpdateContactMutation } from '@/api/endpoints/mainPages';
import { DataSection, InputField } from '@/shared/components';
import { ROUTES } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { CONTACT_VALIDATION_SCHEMA } from './consts';
import styles from './MainContactPage.module.scss';
import type { TFormData } from './types';

interface IProp {
  contactData: IContact | null;
  pageName: string;
  pageId: number;
}

export const MainContactPageComponent = ({ contactData, pageName, pageId }: IProp) => {
  const navigate = useNavigate();

  const { main_text: mainText, additional_text: additionalText } = contactData ?? {};

  const sendApiRequest = useApiRequest();

  const [createContact, { isLoading: isContactCreating }] = useCreateContactMutation();
  const [updateContact, { isLoading: isContactUpdating }] = useUpdateContactMutation();

  const methods = useForm<TFormData>({
    defaultValues: {
      mainText: mainText ?? '',
      additionalText: additionalText ?? '',
    },
    resolver: yupResolver(CONTACT_VALIDATION_SCHEMA),
  });

  const { handleSubmit } = methods;

  const goBack = () => {
    navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`);
  };

  const saveHandler = handleSubmit(({ mainText, additionalText }) => {
    sendApiRequest({
      successMessage: 'Данные успешно сохранены',
      apiRequestCallback: () => {
        const apiRequestCallback = contactData ? updateContact : createContact;

        return apiRequestCallback({
          id: pageId,
          data: {
            main_text: mainText,
            additional_text: additionalText,
          },
        });
      },
    });
  });

  return (
    <div className={styles.wrapper}>
      <Breadcrumb
        items={[
          { title: 'Публичные страницы' },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}`}>Главная</Link> },
          { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.MAIN}/${pageId}`}>{pageName}</Link> },
          { title: 'Контакты' },
        ]}
      />
      <div className={styles['content-wrapper']}>
        <FormProvider {...methods}>
          <DataSection
            titleText="Текст"
            isTitleRequired
          >
            <InputField
              fieldName="mainText"
              placeholder="Текст"
              shouldEnableLengthCounter
              maxLengthCount={500}
            />
          </DataSection>
          <DataSection
            titleText="Почта"
            isTitleRequired
          >
            <InputField
              fieldName="additionalText"
              placeholder="Почта"
              shouldEnableLengthCounter
              maxLengthCount={200}
            />
          </DataSection>
          <div className={styles['buttons-wrapper']}>
            <Button
              onClick={goBack}
              type="dashed"
              size="large"
              className={styles.button}
            >
              Назад
            </Button>
            <Button
              onClick={saveHandler}
              type="primary"
              size="large"
              className={styles.button}
              disabled={isContactCreating || isContactUpdating}
            >
              Сохранить
            </Button>
          </div>
        </FormProvider>
      </div>
    </div>
  );
};
